import type { GetState, SetState } from 'zustand';
import type { EchainProductStore } from './useEchainProductStore';
import type { KetteModel } from '@igus/echain-shared-articles';
import { Length, LengthUnits, Q } from '@igus/echain-shared-quantities';
import { KetteEinspeisung } from '@igus/echain-shared-articles';
import { Kette, KetteConfigurationBuilder } from '@igus/echain-shared-articles-oo';
import type { ProductFragmentEnhanced } from '@models/enhanced-fragments';
import { getEchainLength } from '@libs/echain';
import type { ProductAttributeValueAmountUnit, ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue } from '@libs/attribute.helper';

/**
 * This Zustand slice tries to encapsulate all logic that touches or uses @igus/echain-...-packages.
 * We especially use the length and kette logic from echain domain so here's a short explanation:
 *
 * Objects:
 * - KetteModel: "immutable" new model to represent an echain Kette. We're using a derived type to store our Kette
 *   configuration to be able to use the echain domain calculation methods.
 * - Kette: mutable Kette object that can be used by an instance of KetteConfigurationBuilder to update calculated
 *   properties after static properties of a Kette changed. This object is deprecated and will be replaced by KetteModel
 *   in the future.
 *
 * Utilities:
 * - KetteConfigurationBuilder: Utility class with static methods to build KetteModel instances by specific
 *   attributes like "EffektiveLaenge", "Verfahrweg" or "NumGlieder" that also contains instance methods
 *   to re-calculate properties of Kette objects with changed static attributes like K, K2, Innenbreite etc.
 * - Length: the length model from echain which is used mainly in KetteModel, consisting of value and unit of a length.
 * - LengthUnits: an enum with units that can be assigned to a Length object
 * - Q: this is a helper-object for generating quantifiable objects for echain. The name is short for "Quantity".
 *      We're mainly using it with "value and unit" parameters which creates "Length" instances.
 * - KetteEinspeisung: enum with different types of Einspeisung. This property is required for our calculations but
 *   we only use "mittigeEinspeisung" for now.
 */

const configurationBuilder = new KetteConfigurationBuilder();

export type IComKetteModel = KetteModel;
export type IComKette = Kette;

export enum KetteEinbauArt {
  freitragend = 'freitragend',
  gleitend = 'gleitend',
  haengend = 'haengend',
}

export interface EchainConfigSliceData {
  kette: IComKetteModel;
  einbauArt: KetteEinbauArt;
  isEdit: boolean;
  systemAmount: number;
  cartPositionId?: string;
  hasAussermittigeEinspeisung: boolean;
  versatz?: Length;
  ketteConfigurationFailed?: boolean;
}

export interface EchainConfigSlice extends EchainConfigSliceData {
  setKetteFromProduct: (product: ProductFragmentEnhanced) => void;
  // ConfigurationModel properties
  setEinbauart: (type: KetteEinbauArt) => void;
  // KetteModel properties
  setNumGlieder: (numGlieder: number) => void;
  setEffektiveLaenge: (effektiveLaenge: Length) => void;
  setVerfahrweg: (verfahrweg: Length) => void;
  setIsEdit: (isEdit: boolean) => void;
  setSystemAmount: (amount: number) => void;
  setCartPositionId: (cartPositionId: string) => void;
  setHasAussermittigeEinspeisung: (hasAussermittigeEinspeisung: boolean) => void;
  setVersatz: (versatz: Length) => void;
  setKetteConfigurationFailed: (isFailed: boolean) => void;
}

export const createKetteModel = (json: Partial<IComKetteModel> = {}): IComKetteModel =>
  Kette.fromJson({
    // default values to create an empty, invalid KetteModel
    Teilung: Q(0, LengthUnits.mm).toJson(),
    Versatz: Q(0, LengthUnits.mm).toJson(),
    K: Q(0, LengthUnits.mm).toJson(),
    K2: Q(0, LengthUnits.mm).toJson(),
    Einspeisung: KetteEinspeisung.mittigeEinspeisung,
    FreitragenderBetriebErlaubt: true,
    GleitenderBetriebErlaubt: true,
    // overwrite with given values
    ...json,
  }).toModel();

export const product2Kette = (product: ProductFragmentEnhanced, baseModel?: IComKetteModel): IComKette => {
  const newKette = baseModel ? Kette.fromModel({ ...baseModel }) : Kette.fromModel(createKetteModel());

  newKette.teilung = getEchainLength(
    getAttributeValue<ProductAttributeValueAmountUnit>(
      product.attributes?.pitch_e_chain as ProductAttribute<ProductAttributeValueAmountUnit>
    )
  );
  newKette.k = getEchainLength(
    getAttributeValue<ProductAttributeValueAmountUnit>(
      product.attributes?.K as ProductAttribute<ProductAttributeValueAmountUnit>
    )
  );
  newKette.k2 = getEchainLength(
    getAttributeValue<ProductAttributeValueAmountUnit>(
      product.attributes?.K2 as ProductAttribute<ProductAttributeValueAmountUnit>
    )
  );
  newKette.biegeradius = getEchainLength(
    getAttributeValue<ProductAttributeValueAmountUnit>(
      product.attributes?.bend_radius as ProductAttribute<ProductAttributeValueAmountUnit>
    )
  );
  newKette.innenbreite = getEchainLength(
    getAttributeValue<ProductAttributeValueAmountUnit>(
      product.attributes?.inner_width as ProductAttribute<ProductAttributeValueAmountUnit>
    )
  );

  return newKette;
};

export const createEchainConfigSlice = (
  set: SetState<EchainProductStore>,
  get: GetState<EchainProductStore>,
  initialData?: EchainConfigSliceData
) => {
  // TODO: put initial length for the kette here
  if (initialData?.kette) {
    configurationBuilder.buildByLaengeJson(initialData.kette, Q(1000, LengthUnits.mm));
  }

  return {
    kette: initialData?.kette,
    einbauArt:
      initialData?.einbauArt ?? (initialData?.kette?.LangerWeg ? KetteEinbauArt.gleitend : KetteEinbauArt.freitragend),
    systemAmount: initialData?.systemAmount,
    ketteConfigurationFailed: initialData?.ketteConfigurationFailed,

    setKetteFromProduct: (product: ProductFragmentEnhanced) => {
      set(state => {
        const newKette = product2Kette(product, state.kette);

        try {
          configurationBuilder.update(newKette);
          return {
            ...state,
            kette: newKette.toModel(),
          };
        } catch (error) {
          return {
            ...state,
            ketteConfigurationFailed: true,
          };
        }
      });
    },

    setKetteConfigurationFailed: (isFailed: boolean) => {
      set(state => ({
        ...state,
        ketteConfigurationFailed: isFailed,
      }));
    },

    // setDefaultKette

    setVerfahrweg: (value: Length) => {
      set(state => {
        const newKetteModel = { ...state.kette };
        configurationBuilder.buildByVerfahrwegJson(newKetteModel, value);
        // in contrast to the buildByLaenge method, the buildByVerfahrweg method does not recalculate the Verfahrweg
        // and just set's the value passed to the method. This means we need to manually update the Verfahrweg
        // property with the recalculated value.
        newKetteModel.Verfahrweg = KetteConfigurationBuilder.calcVerfahrwegJson(newKetteModel).toJson();

        return {
          ...state,
          kette: newKetteModel,
        };
      });
    },

    setNumGlieder: (value: number) => {
      set(state => {
        const newKetteModel = { ...state.kette };
        configurationBuilder.buildByNumGliederJson(newKetteModel, value);

        return {
          ...state,
          kette: newKetteModel,
        };
      });
    },

    setEffektiveLaenge: (value: Length) => {
      set(state => {
        const newKetteModel = { ...state.kette };
        configurationBuilder.buildByLaengeJson(newKetteModel, value);

        return {
          ...state,
          kette: newKetteModel,
        };
      });
    },

    setEinbauart: (value: KetteEinbauArt) => {
      set(state => {
        const newKette = Kette.fromModel({ ...state.kette });

        try {
          newKette.langerWeg = value === KetteEinbauArt.gleitend;
          configurationBuilder.buildByVerfahrweg(
            newKette,
            Q.Length.fromJson(state.kette.Verfahrweg ?? Q(0, LengthUnits.mm).toJson())
          );

          return {
            ...state,
            einbauArt: value,
            kette: newKette.toModel(),
          };
        } catch (error) {
          return {
            ...state,
            ketteConfigurationFailed: true,
          };
        }
      });
    },

    setHasAussermittigeEinspeisung: (value: boolean) => {
      set(state => {
        const newKette = Kette.fromModel({ ...state.kette });
        newKette.einspeisung = KetteEinspeisung.aussenmittigeEinspeisung;
        newKette.versatz = value ? state.versatz || Length.mm(0) : Length.mm(0);
        configurationBuilder.buildByVerfahrweg(
          newKette,
          Q.Length.fromJson(state.kette.Verfahrweg ?? Q(0, LengthUnits.mm).toJson())
        );

        return {
          ...state,
          hasAussermittigeEinspeisung: value,
          kette: newKette.toModel(),
        };
      });
    },

    setVersatz: (value: Length) => {
      set(state => {
        const newKette = Kette.fromModel({ ...state.kette });
        newKette.versatz = value;
        configurationBuilder.buildByVerfahrweg(
          newKette,
          Q.Length.fromJson(state.kette.Verfahrweg ?? Q(0, LengthUnits.mm).toJson())
        );

        return {
          ...state,
          versatz: value,
          kette: newKette.toModel(),
        };
      });
    },

    setIsEdit: (isEdit: boolean) => {
      set(state => ({
        ...state,
        isEdit,
      }));
    },

    setSystemAmount: (amount: number) => {
      set(state => ({
        ...state,
        systemAmount: amount,
      }));
    },

    setCartPositionId: (cartPositionId: string) => {
      set(state => ({
        ...state,
        cartPositionId,
      }));
    },
  } as EchainConfigSlice;
};
